import { Link } from 'react-router-dom';
import type { NameConflictCandidate, NameConflictDetails } from '@ni/shared';
import { formatDay, joinClauses, plural } from '../../lib/format';
import { WorkingButton } from '../ui/working-button';
import {
  ALREADY_ON_RECORD,
  NAME_CONFLICT_VIEW_KNOWLEDGE,
  NAME_CONFLICT_VIEW_RECORD,
  nameOnRecordLine,
} from '../../lib/copy';
import './name-conflict.css';

/**
 * **The nouns, and only the nouns** (organisations-as-subjects note §1 R5).
 *
 * The New Organisation door asks this card's exact question — the same name is
 * on file, is this the same one — over the same evidence, so it uses the same
 * card rather than a second one that would drift. What it cannot use is the
 * word "person" four times over, so the four places that say it are props with
 * the person's own wording as the default: New Person passes nothing and renders
 * exactly what it always did.
 */
export interface NameConflictWords {
  /** The paragraph under the title: what each of the two answers means. */
  lead: string;
  /** The force-new button, and its working state. */
  newRecord: string;
  newRecordWorking: string;
  /** The line under that button, naming the case it is for. */
  newRecordNote: string;
  /** What a namesake the org already keeps a record of is tagged. */
  taken: string;
  /**
   * Where a tracked namesake's record page is. Given, each namesake carries a
   * link to look at them first: the record when tracked, the knowledge base
   * entry when only known. Absent, the card shows no links.
   */
  recordPath?: (entityId: string) => string;
}

const PERSON_WORDS: NameConflictWords = {
  lead: nameOnRecordLine('person'),
  newRecord: 'Create a new person',
  newRecordWorking: 'Creating…',
  newRecordNote: 'Different person, same name',
  taken: ALREADY_ON_RECORD,
  recordPath: (entityId) => `/people/${entityId}`,
};

/**
 * Where to look at a namesake before answering: their record when the org
 * tracks them, otherwise their knowledge base entry. A new tab, because the
 * form behind the card still holds what the office typed.
 */
function viewOf(
  candidate: NameConflictCandidate,
  recordPath: (entityId: string) => string,
): { href: string; label: string } {
  return candidate.is_subject
    ? { href: recordPath(candidate.entity_id), label: NAME_CONFLICT_VIEW_RECORD }
    : {
        href: `/knowledge?entity=${encodeURIComponent(candidate.entity_id)}`,
        label: NAME_CONFLICT_VIEW_KNOWLEDGE,
      };
}

/**
 * The duplicate-person decision on New Person (decided 2026-09-01).
 *
 * Research mints people by name — a subject's spouse, her brother — so the
 * person being entered may already be a node in the graph. Entering Andrew East
 * after Shawn Johnson's run had already minted him as her spouse is how prod
 * grew two of him.
 *
 * The panel does not warn and it does not resolve: a name is not an identity,
 * and two live people who share one is a legitimate state. It asks the single
 * question only the office can answer, and gives each namesake the little that
 * honestly tells two strangers apart — who they are tied to, how much is on
 * them, how long the record has stood. No score, no "likely match".
 *
 * It stays in the flow of the form, like the pick list stays under the run
 * strip: the entered prose is still on screen and still the thing being filed,
 * whichever way the question goes.
 */
export function NameConflict({
  details,
  pending,
  held = false,
  words = PERSON_WORDS,
  onUse,
  onCreateNew,
}: {
  details: NameConflictDetails;
  /** The entity id being adopted, `'new'` while forcing a namesake, else null. */
  pending: string | null;
  /**
   * Whether the form behind this card is in a state it cannot be filed from —
   * today, a prose box past the note cap (2026-09-13).
   *
   * **The card's two answers are the create**, so they are exactly as capped as
   * the Create button they replace. Without this the cap had a door left open:
   * the fields stay live while the card is up, so a long paste made *after* the
   * refusal would ride out on "Use this record" — the one button still on the
   * screen. Held rather than hidden, and the count and its line are still up the
   * page where the paste went, so a card that will not answer says why.
   */
  held?: boolean;
  /** The kind's own nouns. Absent is a person, which is where this card began. */
  words?: NameConflictWords;
  onUse: (entityId: string) => void;
  onCreateNew: () => void;
}) {
  const busy = pending !== null;

  return (
    <div className="name-conflict">
      <div className="conflict-head">
        <div className="conflict-title">{details.name} is already on record</div>
        <p>{words.lead}</p>
      </div>

      <div className="candidates">
        {details.candidates.map((candidate) => (
          <NameConflictCandidateCard
            key={candidate.entity_id}
            candidate={candidate}
            working={pending === candidate.entity_id}
            disabled={held || (busy && pending !== candidate.entity_id)}
            takenLabel={words.taken}
            view={words.recordPath ? viewOf(candidate, words.recordPath) : undefined}
            onUse={() => onUse(candidate.entity_id)}
          />
        ))}
      </div>

      <div className="conflict-foot">
        <WorkingButton
          label={words.newRecord}
          workingLabel={words.newRecordWorking}
          variant="act"
          working={pending === 'new'}
          disabled={held || (busy && pending !== 'new')}
          onClick={onCreateNew}
        />
        <div className="field-note">{words.newRecordNote}</div>
      </div>
    </div>
  );
}

/**
 * **One namesake, with the little that honestly tells two strangers apart.**
 *
 * Exported since 2026-09-06 because the import's People tab asks the identical
 * question from a different door (import note §1.8): an `ambiguous` row carries
 * `NameConflictCandidate[]`, deliberately the same shape as this one, and the
 * office answers it with the same card. Only the button's words differ, which is
 * why they are props — the same reason `candidates/candidate-card.tsx` takes
 * them.
 *
 * Note this is **not** that file's `CandidateCard`: that one renders a resolver
 * `Candidate`, which carries a match basis and near-match corrections. This one
 * renders a live namesake, which carries none of that because the collision is
 * exact-name and the judgement is the reader's.
 */
export function NameConflictCandidateCard({
  candidate,
  working,
  disabled,
  onUse,
  label = 'Use this record',
  workingLabel = 'Using…',
  takenLabel = ALREADY_ON_RECORD,
  subjectsPickable = false,
  view,
}: {
  candidate: NameConflictCandidate;
  working: boolean;
  disabled: boolean;
  onUse: () => void;
  /** "Use this record" on New Person, "This is them" on an import row. */
  label?: string;
  workingLabel?: string;
  /**
   * What a namesake the org already keeps a record of is tagged. "Already a
   * subject" is a person's word — an organisation has no status and no vertical
   * vocabulary, so its door says "Already on file" instead.
   */
  takenLabel?: string;
  /**
   * True on an import row, where the question is "which person on record is
   * this row", and a tracked person is the likeliest answer: the pick attaches
   * the row to them. New Person leaves it off, because it would adopt them.
   */
  subjectsPickable?: boolean;
  /** A link to look at this namesake, opened in a new tab. */
  view?: { href: string; label: string };
}) {
  /* On New Person, someone who is already a subject cannot be adopted — they
     have the record this form would create. The card stays listed rather than
     being filtered out: it is often the very row that tells the reader this is
     the person they meant, and it is the one they should be opening instead. */
  const taken = candidate.is_subject && !subjectsPickable;

  return (
    <div className={taken ? 'candidate off' : 'candidate'}>
      <div className="who">
        <div className="cname">{candidate.display_name}</div>
        {candidate.relationships.map((line, index) => (
          <div className="cdetail" key={`${candidate.entity_id}-tie-${index}`}>
            {line}
          </div>
        ))}
        <div className="src">
          {joinClauses([
            plural(candidate.fact_count, 'record', 'records'),
            `added ${formatDay(candidate.created_at)}`,
          ])}
        </div>
        {view ? (
          <Link className="cview" to={view.href} target="_blank" rel="noopener noreferrer">
            {view.label}
          </Link>
        ) : null}
      </div>
      {taken ? (
        <span className="tag-faint">{takenLabel}</span>
      ) : (
        <WorkingButton
          label={label}
          workingLabel={workingLabel}
          working={working}
          disabled={disabled}
          onClick={onUse}
        />
      )}
    </div>
  );
}
